import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import { router } from "@inertiajs/react";
import { Check } from "lucide-react";

import { theme } from "./theme";
import { apiFetch } from "./api";
import { useT } from "./i18n";

// Centered top-bar status, two fixed slots that never collapse the layout:
//   1. Refresh — countdown ring to the page's content auto-refresh (only on pages that poll).
//   2. Cooldown — the eZee availability-push cooldown (FrontdeskV2::AvailabilityCooldownStatus).
//      Green/idle when clear, amber while a cooldown is active, with a hover popover breaking it down
//      per room type + that room type's walk-in rate. Both self-tick every second; the cooldown polls
//      a small endpoint so it stays fresh on every page.
const REFRESH_GOLD = "#e6c680";
const COOLDOWN_AMBER = "#f0a33c";
const IDLE_GREEN = "#3fbf7f";
const COOLDOWN_POLL_MS = 15_000;
const RING_SIZE = 28;
const RING_STROKE = 3;
const RADIUS = (RING_SIZE - RING_STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const fmtSeconds = (total) => {
  if (total >= 60) {
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  }
  return `${total}s`;
};

const Wrap = styled.div`
  display: flex;
  align-items: center;
  gap: 1.1rem;
  @media (max-width: 900px) {
    display: none;
  }
`;

const Item = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const RingWrap = styled.div`
  position: relative;
  width: ${RING_SIZE}px;
  height: ${RING_SIZE}px;
  flex: 0 0 auto;
`;

const Progress = styled.circle`
  transition: stroke-dashoffset 0.95s linear;
`;

const RingLabel = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.58rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #ffffff;
`;

const Caption = styled.span`
  display: flex;
  flex-direction: column;
  line-height: 1.15;
`;

const CaptionMain = styled.span`
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.88);
`;

const CaptionSub = styled.span`
  font-size: 0.56rem;
  font-weight: 600;
  color: ${(props) => props.$color || "rgba(255, 255, 255, 0.55)"};
`;

const Pop = styled.div`
  position: absolute;
  top: calc(100% + 0.55rem);
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  width: 260px;
  max-width: calc(100vw - 2rem);
  padding: 0.55rem 0.65rem;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  box-shadow: 0 16px 36px -14px rgba(26, 26, 26, 0.4);
`;

const PopTitle = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${theme.muted};
  margin-bottom: 0.35rem;
`;

const PopRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.28rem 0.1rem;
  border-bottom: 1px solid ${theme.border};
  &:last-child {
    border-bottom: 0;
  }
`;

const PopCode = styled.span`
  font-weight: 800;
  color: ${theme.ink};
  font-size: 0.82rem;
`;

const PopRemaining = styled.span`
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: ${COOLDOWN_AMBER};
  font-size: 0.78rem;
`;

const Ring = (props) => {
  const clamped = Math.max(0, Math.min(1, props.progress));
  const offset = CIRCUMFERENCE * (1 - clamped);
  const center = RING_SIZE / 2;
  return (
    <RingWrap>
      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
      >
        <circle
          cx={center}
          cy={center}
          r={RADIUS}
          fill="none"
          stroke="rgba(255, 255, 255, 0.18)"
          strokeWidth={RING_STROKE}
        />
        <Progress
          cx={center}
          cy={center}
          r={RADIUS}
          fill="none"
          stroke={props.color}
          strokeWidth={RING_STROKE}
          strokeLinecap="round"
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <RingLabel>{props.label}</RingLabel>
    </RingWrap>
  );
};

const TopBarStatus = (props) => {
  const { t } = useT();
  const totalSeconds = props.refreshMs ? Math.round(props.refreshMs / 1000) : 0;
  const [refreshLeft, setRefreshLeft] = useState(totalSeconds);
  const [cooldown, setCooldown] = useState({
    active: false,
    remaining: 0,
    full: 0,
    roomTypes: [],
  });
  const [popOpen, setPopOpen] = useState(false);
  const cooldownRef = useRef(cooldown);
  cooldownRef.current = cooldown;

  // Content auto-refresh countdown: tick down each second, wrap at zero, and resync to the real poll
  // whenever an Inertia visit finishes (the page's usePoll reload, or a navigation).
  useEffect(() => {
    if (!totalSeconds) {
      return undefined;
    }
    const tick = setInterval(() => {
      setRefreshLeft((left) => (left <= 1 ? totalSeconds : left - 1));
    }, 1000);
    const stop = router.on("finish", () => setRefreshLeft(totalSeconds));
    return () => {
      clearInterval(tick);
      stop();
    };
  }, [totalSeconds]);

  // Availability cooldown: poll the endpoint, and tick the max-remaining down between polls so the
  // number decreases smoothly and clears itself at zero.
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await apiFetch("/frontdesk_v2/availability_status");
        if (!mounted || !res?.data) {
          return;
        }
        setCooldown({
          active: Boolean(res.data.active),
          remaining: res.data.remaining_seconds || 0,
          full: res.data.full_seconds || 0,
          roomTypes: res.data.room_types || [],
        });
      } catch {
        // A transient failure just leaves the last snapshot ticking down; next poll recovers.
      }
    };
    load();
    const poll = setInterval(load, COOLDOWN_POLL_MS);
    const tick = setInterval(() => {
      const current = cooldownRef.current;
      if (!current.active) {
        return;
      }
      const remaining = Math.max(0, current.remaining - 1);
      const roomTypes = current.roomTypes
        .map((room) => ({
          ...room,
          remaining_seconds: Math.max(0, room.remaining_seconds - 1),
        }))
        .filter((room) => room.remaining_seconds > 0);
      setCooldown({
        ...current,
        active: remaining > 0,
        remaining,
        roomTypes,
      });
    }, 1000);
    return () => {
      mounted = false;
      clearInterval(poll);
      clearInterval(tick);
    };
  }, []);

  const showRefresh = totalSeconds > 0;
  const cooldownScale = cooldown.full > 0 ? cooldown.full : 1;
  const showPop = popOpen && cooldown.active && cooldown.roomTypes.length > 0;

  return (
    <Wrap>
      {showRefresh ? (
        <Item>
          <Ring
            progress={refreshLeft / totalSeconds}
            color={REFRESH_GOLD}
            label={refreshLeft}
          />
          <Caption>
            <CaptionMain>{t("tb_refresh")}</CaptionMain>
            <CaptionSub>{t("tb_refresh_sub")}</CaptionSub>
          </Caption>
        </Item>
      ) : null}

      <Item
        title={t("tb_cooldown_title")}
        onMouseEnter={() => setPopOpen(true)}
        onMouseLeave={() => setPopOpen(false)}
      >
        {cooldown.active ? (
          <Ring
            progress={cooldown.remaining / cooldownScale}
            color={COOLDOWN_AMBER}
            label={fmtSeconds(cooldown.remaining)}
          />
        ) : (
          <Ring progress={1} color={IDLE_GREEN} label={<Check size={13} />} />
        )}
        <Caption>
          <CaptionMain>{t("tb_cooldown")}</CaptionMain>
          {cooldown.active ? (
            <CaptionSub $color={COOLDOWN_AMBER}>
              {cooldown.roomTypes.length} {t("tb_cooldown_types")}
            </CaptionSub>
          ) : (
            <CaptionSub $color={IDLE_GREEN}>{t("tb_cooldown_idle")}</CaptionSub>
          )}
        </Caption>

        {showPop ? (
          <Pop>
            <PopTitle>{t("tb_cooldown_pop_title")}</PopTitle>
            {cooldown.roomTypes.map((room) => (
              <PopRow key={room.code}>
                <PopCode>{room.code}</PopCode>
                <PopRemaining>
                  {fmtSeconds(room.remaining_seconds)}
                </PopRemaining>
              </PopRow>
            ))}
          </Pop>
        ) : null}
      </Item>
    </Wrap>
  );
};

export default TopBarStatus;
